import { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "react-bootstrap";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import { useV2CreateBlueprintMutation } from "@generated";
import BlueprintGeneralFields from "../common/BlueprintGeneralFields/BlueprintGeneralFields";
import { BlueprintFormState, defaultBlueprintFormState } from "../common/types";
import {
  useGetAccount,
  useListRateTables,
  useListPaymentTerms,
  useListPhases,
  useListResources,
} from "../common/api";
import "../common/formStyle.css";

const New = (): JSX.Element => {
  const navigate = useNavigate();
  const [form, setForm] = useState<BlueprintFormState>(
    defaultBlueprintFormState
  );
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);

  const [createBlueprint] = useV2CreateBlueprintMutation();
  const { accountId, accountLoading } = useGetAccount();
  const { rateTables: rateTableOptions, rateTablesLoading } =
    useListRateTables();
  const { paymentTerms: paymentTermOptions, paymentTermsLoading } =
    useListPaymentTerms();
  const { phases, phasesLoading } = useListPhases();
  const { resources, resourcesLoading } = useListResources();

  const handleSave = () => {
    if (!form.name.trim()) {
      setErrorMessage("Blueprint name is required.");
      setShowFailAlert(true);
      return;
    }

    const rateTableId = form.rateTable || rateTableOptions[0]?.value;
    const paymentTermId = form.paymentTerm || paymentTermOptions[0]?.value;

    const payload: any[] = [
      {
        id: crypto.randomUUID(),
        type: "projects",
        attributes: {
          "executive-summary": form.executiveSummary || "",
          "solution-summary": form.solutionSummary || "",
        },
        relationships: {
          "rate-table": rateTableId
            ? { data: { type: "rate-tables", id: Number(rateTableId) } }
            : null,
          "payment-term": paymentTermId
            ? { data: { type: "payment-terms", id: Number(paymentTermId) } }
            : null,
        },
      },
      ...phases.map((p) => ({
        lid: p.id,
        type: "project-phases",
        attributes: {
          name: p.name,
          position: p.position,
        },
        relationships: {
          phase: { data: { type: "phases", id: Number(p.id) } },
        },
      })),
      ...resources.map((r) => ({
        lid: r.id,
        type: "project-resources",
        attributes: r.attributes,
        relationships: {
          resource: { data: { type: "resources", id: Number(r.id) } },
        },
      })),
    ];

    setLoading(true);
    createBlueprint({
      body: {
        data: {
          type: "blueprints",
          attributes: {
            name: form.name.trim(),
            "tag-list": form.tags,
            ...(payload.length ? { payload: payload as any } : {}),
          },
          relationships: {
            account: { data: { type: "accounts", id: String(accountId) } },
          },
        },
      },
    })
      .unwrap()
      .then((res) => {
        const id = res?.data?.id;
        if (id) {
          navigate(`/admin/blueprints/${id}/edit`);
        } else {
          setErrorMessage("Failed to create blueprint.");
          setShowFailAlert(true);
          setLoading(false);
        }
      })
      .catch((err) => {
        setErrorMessage(
          err?.data?.errors?.[0]?.detail || "Something went wrong."
        );
        setShowFailAlert(true);
        setLoading(false);
      });
  };

  if (
    accountLoading ||
    rateTablesLoading ||
    paymentTermsLoading ||
    phasesLoading ||
    resourcesLoading
  ) {
    return <ScopeStackSpinner />;
  }

  return (
    <div className="blueprintForm legacyPageBleed">
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <div className="headerContainer">
        <div className="actionBtns">
          <Button
            className="cancelButton"
            onClick={() => navigate("/admin/blueprints")}
          >
            Cancel
          </Button>
          <h2>Create Blueprint</h2>
        </div>
        <div className="actionBtns">
          <Button className="button" onClick={handleSave} disabled={loading}>
            {loading ? <SmallSpinner /> : "Save"}
          </Button>
        </div>
      </div>
      <BlueprintGeneralFields
        form={form}
        setForm={setForm}
        rateTableOptions={rateTableOptions}
        paymentTermOptions={paymentTermOptions}
      />
    </div>
  );
};

export default New;
